import React from 'react';

import { Accordion, Badge, Group, Paper, Radio, ScrollArea, Stack, Text, Title } from '@mantine/core';
import dayjs from 'dayjs';
import { AthleteEvent } from 'types';

import { useChangeModal } from '../../ChangeModalContext';
import { ReservationChoice } from '../../changeModalSteps';

const formatEventDateTime = (event: AthleteEvent): string => {
  const dt = dayjs(`${event.startDate}T${event.startTime}`);
  return dt.isValid()
    ? dt.format('ddd, MMM D, YYYY · h:mm A')
    : dayjs(event.startDate).format('ddd, MMM D, YYYY');
};

const ReservationRow: React.FC<{ event: AthleteEvent }> = ({ event }) => (
  <Group position='apart' noWrap spacing='sm'>
    <Stack spacing={0} sx={{ minWidth: 0 }}>
      <Text size='sm' fw={500} truncate>
        {event.name}
      </Text>
      <Text size='xs' c='dimmed'>
        {formatEventDateTime(event)}
        {event.coachName ? ` · ${event.coachName}` : ''}
      </Text>
    </Stack>
    {event.isCredited ? (
      <Badge size='xs' color='blue' variant='light'>
        Credited
      </Badge>
    ) : (
      <Badge size='xs' color='gray' variant='light'>
        Uncredited
      </Badge>
    )}
  </Group>
);

const ReservationHandlingStep: React.FC = () => {
  const { form, upcomingTotalCount, upcomingReservations } = useChangeModal();

  return (
    <Stack>
      <Paper p='md' withBorder>
        <Stack spacing={4}>
          <Title order={5}>Upcoming reservations</Title>
          <Text size='sm'>Choose what should happen with reservations after the change date.</Text>
          <Text size='sm' c='dimmed'>
            This package has {upcomingTotalCount} upcoming reservation(s) after the change date.
          </Text>
        </Stack>
      </Paper>

      {upcomingReservations.length > 0 && (
        <Accordion variant='contained' radius='sm' chevronPosition='right'>
          <Accordion.Item value='affected-reservations' sx={{ border: 'none' }}>
            <Accordion.Control px='md' py='xs'>
              <Group spacing='xs'>
                <Text size='xs' fw={600} tt='uppercase' c='dimmed'>
                  Affected reservations
                </Text>
                <Badge size='xs' variant='filled' color='gray'>
                  {upcomingReservations.length}
                </Badge>
              </Group>
            </Accordion.Control>
            <Accordion.Panel px='md' pb='md'>
              <ScrollArea h={Math.min(200, upcomingReservations.length * 56)} type='auto' offsetScrollbars>
                <Stack spacing='xs'>
                  {upcomingReservations.map(event => (
                    <ReservationRow key={event.athleteEventId} event={event} />
                  ))}
                </Stack>
              </ScrollArea>
            </Accordion.Panel>
          </Accordion.Item>
        </Accordion>
      )}

      <Radio.Group
        value={form.values.reservationChoice ?? ''}
        onChange={value => form.setFieldValue('reservationChoice', value as ReservationChoice)}
        error={form.errors.reservationChoice}
        name='reservationChoice'
      >
        <Stack spacing='sm'>
          <Paper p='md' withBorder>
            <Radio
              value='KEEP'
              label={
                <Stack spacing={2}>
                  <Text fw={600}>Keep existing reservations</Text>
                  <Text size='sm' c='dimmed'>
                    Reservations stay booked. They will be re-credited to the new package once it is active.
                  </Text>
                </Stack>
              }
            />
          </Paper>

          <Paper p='md' withBorder>
            <Radio
              value='DELETE'
              label={
                <Stack spacing={2}>
                  <Text fw={600}>Delete future reservations</Text>
                  <Text size='sm' c='dimmed'>
                    All upcoming reservations after the change date will be removed.
                  </Text>
                </Stack>
              }
            />
          </Paper>
        </Stack>
      </Radio.Group>
    </Stack>
  );
};

export default ReservationHandlingStep;
